import { X } from 'lucide-react';
import { type ComponentProps, type KeyboardEvent, useId, useMemo, useRef, useState } from 'react';

import { cn } from '../lib/utils';
import { Badge } from './ui/badge';

export interface TagInputProps
  extends Pick<
    ComponentProps<'input'>,
    'id' | 'aria-label' | 'aria-labelledby' | 'aria-describedby' | 'aria-invalid'
  > {
  value: string[];
  onChange: (next: string[]) => void;
  /** Existing tags to pick from, narrowed by what has been typed; tags already added are left out. */
  suggestions?: string[];
  placeholder?: string;
  disabled?: boolean;
  maxTags?: number;
}

const ADD_KEYS = ['Enter', ','];

/**
 * A controlled list of free-text tags. Text is trimmed and never empty, and a
 * duplicate is ignored case-insensitively, so callers can store `value`
 * as-is without re-normalising it.
 */
export function TagInput({
  id,
  value,
  onChange,
  suggestions = [],
  placeholder,
  disabled = false,
  maxTags,
  ...aria
}: TagInputProps) {
  const [draft, setDraft] = useState('');
  const [focused, setFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const atLimit = maxTags !== undefined && value.length >= maxTags;
  const query = draft.trim().toLowerCase();

  const matches = useMemo(() => {
    if (!query) return [];
    const taken = new Set(value.map((tag) => tag.toLowerCase()));

    return suggestions.filter(
      (suggestion) =>
        !taken.has(suggestion.toLowerCase()) && suggestion.toLowerCase().includes(query),
    );
  }, [query, suggestions, value]);

  const showSuggestions = focused && !disabled && !atLimit && matches.length > 0;

  function addTag(raw: string): void {
    const text = raw.trim();
    setDraft('');
    if (!text || atLimit) return;
    if (value.some((tag) => tag.toLowerCase() === text.toLowerCase())) return;

    onChange([...value, text]);
  }

  function removeTag(index: number): void {
    onChange(value.filter((_, position) => position !== index));
    inputRef.current?.focus();
  }

  function handleSuggestionNavigation(event: KeyboardEvent<HTMLInputElement>): boolean {
    if (!showSuggestions) return false;

    if (event.key === 'ArrowDown') {
      setActiveIndex((index) => Math.min(index + 1, matches.length - 1));
    } else if (event.key === 'ArrowUp') {
      setActiveIndex((index) => Math.max(index - 1, -1));
    } else if (event.key === 'Escape') {
      setFocused(false);
    } else {
      return false;
    }

    event.preventDefault();
    return true;
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (handleSuggestionNavigation(event)) return;

    if (ADD_KEYS.includes(event.key)) {
      // Enter must never submit the surrounding form, even with nothing typed.
      event.preventDefault();
      addTag(showSuggestions && activeIndex >= 0 ? (matches[activeIndex] ?? draft) : draft);
    } else if (event.key === 'Backspace' && draft === '' && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  }

  return (
    <div className="relative">
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: the click only forwards focus to the real input inside */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: same — a pointer convenience, not a control */}
      <div
        onClick={() => inputRef.current?.focus()}
        className={cn(
          'flex min-h-9 w-full flex-wrap items-center gap-1.5 rounded-md border border-input bg-card px-2 py-1.5 text-sm shadow-xs transition-[color,box-shadow,border-color,background-color] duration-150 ease-out hover:border-ring/40 focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50 focus-within:ring-offset-2 focus-within:ring-offset-background',
          aria['aria-invalid'] && 'border-destructive ring-destructive/20',
          disabled && 'pointer-events-none cursor-not-allowed opacity-50',
        )}
      >
        {value.map((tag, index) => (
          <Badge key={tag.toLowerCase()} variant="secondary" className="max-w-full gap-1 pr-1">
            <span className="truncate">{tag}</span>
            <button
              type="button"
              disabled={disabled}
              aria-label={`Remove tag ${tag}`}
              onClick={(event) => {
                event.stopPropagation();
                removeTag(index);
              }}
              className="rounded-full p-0.5 outline-none hover:bg-foreground/10 focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-3" />
            </button>
          </Badge>
        ))}

        <input
          {...aria}
          ref={inputRef}
          id={id}
          value={draft}
          disabled={disabled || atLimit}
          placeholder={value.length === 0 ? placeholder : undefined}
          role="combobox"
          aria-expanded={showSuggestions}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
          onChange={(event) => {
            setDraft(event.target.value);
            setActiveIndex(-1);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            // Text left behind on blur would be lost silently otherwise.
            addTag(draft);
          }}
          className="min-w-24 flex-1 bg-transparent py-0.5 text-base outline-none placeholder:text-placeholder md:text-sm"
        />
      </div>

      <div
        id={listId}
        role="listbox"
        aria-label="Suggested tags"
        hidden={!showSuggestions}
        className="absolute inset-x-0 top-full z-50 mt-1 max-h-56 overflow-y-auto rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md"
      >
        {matches.map((suggestion, index) => (
          <div
            key={suggestion}
            id={`${listId}-${index}`}
            role="option"
            tabIndex={-1}
            aria-selected={index === activeIndex}
            // Mouse-down, not click: click fires after blur has already closed the list.
            onMouseDown={(event) => {
              event.preventDefault();
              addTag(suggestion);
            }}
            onMouseEnter={() => setActiveIndex(index)}
            className={cn(
              'cursor-pointer truncate rounded-sm px-2 py-1.5 text-sm select-none',
              index === activeIndex && 'bg-accent text-accent-foreground',
            )}
          >
            {suggestion}
          </div>
        ))}
      </div>
    </div>
  );
}
